import type { RequestOptions } from "mixpanel-browser";
import type { DictationAcceptTrigger, HomeViewMode } from "shared";
import { addMixpanelEvent } from "utilz/helpers";
import type {
  DashboardScopeMode,
  HomeOverviewData,
  OverviewPeriod,
} from "../types";
import { aiHomeSessionId } from "./aiHomeSession";
import { aiHomeTimeContext } from "./timeContext";

export type AiQueryTrigger = "composer" | "suggested_prompt";
export type AiNewChatTrigger = "new_chat_button";
export type AiResponseType = "text" | "table" | "confirmation";
export type AiFailureType =
  | "error"
  | "timeout"
  | "stream_incomplete"
  | "user_stopped"
  | "navigated_away"
  | `http_${number}`;
export type AiTurnResumeMethod = "refetch" | "abandoned";
export type AiChatHistorySource =
  | "pinned"
  | "recent_today"
  | "recent_yesterday"
  | "previous_7_days"
  | "older"
  | "search";
export type AiResultActionType =
  | "approve_pending_write"
  | "decline_pending_write";
export type AiSuggestionType =
  | "sla_risk"
  | "unassigned_tickets"
  | "critical_high_priority_tickets"
  | "my-workload"
  | "create_ticket"
  | "create_customer"
  | "create_user"
  | "other";
export type HomeOverviewCardType =
  | "at_risk"
  | "overdue"
  | "unresolved"
  | "unassigned";
export type AiDictationAcceptTrigger = DictationAcceptTrigger;
export type ScreenToggleDirection = "ai_to_legacy" | "legacy_to_ai";
export type ScopeToggleDirection = "me_to_team" | "team_to_me";

interface AiHomeEventPayloads {
  ai_home_screen_viewed: {
    screen_mode: HomeViewMode;
    is_first_visit: boolean;
  };
  ai_screen_toggled: {
    direction: ScreenToggleDirection;
    time_in_session_ms: number;
    query_count_before_toggle: number;
  };
  use_ai_screen_clicked: {
    source: "profile_menu";
    current_screen: HomeViewMode;
  };
  use_legacy_screen_clicked: {
    source: "profile_menu";
    current_screen: HomeViewMode;
  };
  ai_session_ended: {
    screen_mode_at_exit: HomeViewMode;
    total_queries: number;
    total_chats_opened: number;
    session_duration_ms: number;
    last_action: string | null;
  };
  ai_new_chat_started: {
    trigger: AiNewChatTrigger;
    previous_chat_id: string | null;
  };
  ai_query_submitted: {
    chat_id: string | null;
    query_id: string;
    query_text: string;
    query_text_scrubbed: boolean;
    query_length: number;
    query_index: number;
    is_first_query: boolean;
    is_new_chat: boolean;
    trigger: AiQueryTrigger;
  };
  ai_response_received: {
    chat_id: string | null;
    query_id: string;
    response_type: AiResponseType;
    response_time_ms: number;
    time_to_first_token_ms: number | null;
    answer_length: number;
    tool_count: number;
    has_table: boolean;
  };
  ai_response_failed: {
    chat_id: string | null;
    query_id: string;
    query_text: string;
    query_text_scrubbed: boolean;
    failure_type: AiFailureType;
    response_time_ms: number;
  };
  ai_turn_interrupted: {
    chat_id: string | null;
    query_id: string | null;
    elapsed_ms: number;
    had_conversation_id: boolean;
    answer_length: number;
  };
  ai_turn_resumed: {
    chat_id: string;
    method: AiTurnResumeMethod;
    gap_ms: number;
    recovered: boolean;
    attempts: number;
    is_manual: boolean;
  };
  ai_result_action_taken: {
    chat_id: string | null;
    action_type: AiResultActionType;
    result_type: string | null;
    item_id: string | null;
  };
  ai_chat_pinned: {
    chat_id: string;
    chat_title: string | null;
    query_count_in_chat: number | null;
  };
  ai_chat_unpinned: {
    chat_id: string;
    chat_title: string | null;
  };
  ai_chat_searched: {
    search_text: string;
    search_text_scrubbed: boolean;
    results_count: number;
  };
  ai_chat_opened_from_history: {
    chat_id: string;
    source: AiChatHistorySource;
    chat_age_days: number | null;
  };
  ai_dictation_mic_clicked: Record<string, never>;
  ai_dictation_accepted: {
    voice_session_id: string;
    trigger: AiDictationAcceptTrigger;
    text_length: number;
    duration_seconds: number;
  };
  ai_dictation_rejected: {
    duration_seconds: number;
    transcript_length: number;
  };
  ai_proactive_suggestion_clicked: {
    suggestion_text: string;
    suggestion_type: AiSuggestionType;
    position: number;
  };
  calendar_event_clicked: {
    event_id: string;
    event_type: string;
    source: "home_calendar_panel";
  };
  home_overview_card_clicked: {
    card_type: HomeOverviewCardType;
    value_shown: number;
    time_filter: OverviewPeriod;
    scope_mode: DashboardScopeMode;
    screen_mode: HomeViewMode;
  };
  home_time_filter_changed: {
    previous_filter: OverviewPeriod;
    new_filter: OverviewPeriod;
  };
  home_me_team_toggled: {
    direction: ScopeToggleDirection;
    scope_mode: DashboardScopeMode;
    resource_count: number;
    time_in_session_ms: number;
  };
}

export const trackAiHome = <K extends keyof AiHomeEventPayloads>(
  event: K,
  data: AiHomeEventPayloads[K],
  options?: RequestOptions,
) =>
  addMixpanelEvent({
    event,
    data: {
      session_id: aiHomeSessionId(),
      ...aiHomeTimeContext(),
      ...data,
    },
    options,
  });

export const toOverviewCardType = (
  id: keyof HomeOverviewData,
): HomeOverviewCardType => {
  switch (id) {
    case "at_risk_count":
      return "at_risk";
    case "overdue_count":
      return "overdue";
    case "unassigned_count":
      return "unassigned";
    default:
      return "unresolved";
  }
};
